import { ofetch, type $Fetch } from 'ofetch';
import { DomainError } from '@/domain/error/DomainError';
import type { InvitationGateway } from '@/application/port/out/InvitationGateway';
import type {
  Invitation,
  InvitationStatus,
  CreateInvitationCommand,
  AcceptInvitationCommand,
  AcceptedInvitation,
} from '@/domain/model/Invitation';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const ROOT = '/api/v1/invitations';

/** HTTP gateway de Convites. Authority BE: `identity:invitation:*`. */
export class HttpInvitationGateway implements InvitationGateway {
  constructor(
    private readonly client: $Fetch,
    private readonly baseURL: string,
  ) {}

  async accept(command: AcceptInvitationCommand): Promise<AcceptedInvitation> {
    try {
      const response = await ofetch<ApiResponse<AcceptedInvitation>>('/api/v1/auth/accept-invite', {
        baseURL: this.baseURL,
        method: 'POST',
        body: command,
        retry: 0,
      });
      assertApiSuccess(response);
      return response.data;
    } catch (failure) {
      if (failure instanceof DomainError) throw failure;
      const error = failure as { data?: unknown; status?: number; statusCode?: number };
      throw DomainError.fromApiError(error.data, error.status ?? error.statusCode ?? 500);
    }
  }

  async list(status?: InvitationStatus): Promise<Invitation[]> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    const res = await this.client<ApiResponse<Invitation[]>>(ROOT, { method: 'GET', params });
    assertApiSuccess(res);
    return res.data;
  }

  async create(cmd: CreateInvitationCommand): Promise<Invitation> {
    const res = await this.client<ApiResponse<Invitation>>(ROOT, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async resend(id: string): Promise<Invitation> {
    const res = await this.client<ApiResponse<Invitation>>(
      `${ROOT}/${encodeURIComponent(id)}/resend`,
      { method: 'POST' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async revoke(id: string): Promise<void> {
    // BE 204 No Content — sem envelope.
    await this.client<void>(`${ROOT}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}
